import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useCartItemId } from 'features/ShoppingCart/hooks';
import { useTranslation } from 'react-i18next';
import { useIsMobile, useIsTablet } from 'WindowDimensionProvider';
import useOneCartItemListing from 'hooks/useOneCartItemListing';
import { useCart } from 'hooks/useCart';
import { CartItemState } from 'types/cart';
import useSearchQueryParams from 'hooks/use-search-params';
import { NavigationHeader } from 'components/NavigationHeader';
import { Flex, Sep } from 'components/common/styled/common-styled';
import { PageWithSidebar, Wrapper } from 'components/theme/Wrapper';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button/Button';
import {
  ResourceHeading2,
  ResourceParagraph,
} from 'components/theme/Typography/ResourceTypography';
import ListingCartItemSummary from 'features/ShoppingCart/components/ListingCartItemSummary';
import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  AVAILABLE_BRAND_FEATURES,
  AddOnV3,
  ListingAddOnUnavailableReason,
} from '@kouto/types';
import {
  analyticsManager,
  AnalyticsProduct,
  getAnalyticsDataFromCartItem,
} from 'features/analytics';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import useCancellationPolicy from 'hooks/useCancellationPolicy';
import { PageTitle } from 'components/atoms/PageTitle';
import { useAppState } from 'AppProvider';
import { isFeatureActive } from 'utils/featureFlag';
import hasParticipantOrCustomQuestion from 'utils/customQuestions/hasParticipantOrCustomQuestion';
import useCustomQuestions from 'api/experience/useCustomQuestions/useCustomQuestions';
import useListingAddOnAvailability from 'api/listing/useListingAddOnAvailability';
import useCartItemStayPrices from 'hooks/useCartItemStayPrices';
import useResourceOptions from 'api/experience/useResourceOptions';
import { hasResourceOptionsForParticipants } from 'utils/resourceSelections/resourceSelections';
import AddOnsPageLoading from './components/AddOnsPageLoading';
import AddOnsList from './components/AddOnsList';
import isAddOnBookable from './isAddOnBookable';

const SelectAddOnsPage = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { onEditCart: editCart } = useCart();
  const { t: translate } = useTranslation();
  const { searchParams } = useSearchQueryParams();
  const cartItemId = useCartItemId();
  const { brand } = useAppState();

  const { cartItem, listing, isLoading } = useOneCartItemListing(cartItemId);
  const { prices: stayPrices } = useCartItemStayPrices(cartItem, listing);
  const { data: addOnAvailability, isLoading: isLoadingAddOnAvailability } =
    useListingAddOnAvailability({
      listingId: cartItem?.listingId,
      experienceId: cartItem?.experienceId,
      startDateTime: cartItem?.sessionDateTime,
      sessionDuration: cartItem?.sessionDuration,
    });

  const { data: customQuestions } = useCustomQuestions({
    brandId: listing?.brandId,
    experienceId: cartItem?.experienceId,
    includeDefault: true,
  });

  const { hasCustomQuestions, askParticipantsInfo } =
    hasParticipantOrCustomQuestion(customQuestions);

  const { data: resourceOptions, isLoading: isLoadingResourceOptions } =
    useResourceOptions({
      experienceId: cartItem?.experienceId,
      startDateTime: cartItem?.sessionDateTime,
      sessionDuration: cartItem?.sessionDuration,
    });
  const hasResourceOptions = hasResourceOptionsForParticipants(
    cartItem?.participants ?? [],
    resourceOptions ?? [],
  );

  const [addOnsAmounts, setAddOnsAmounts] = useState<Record<string, number>>(
    {},
  );

  const hasRemoveAddToCart = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_HIDE_ADD_TO_CART_BUTTON,
  );

  const { cancellationPolicy } = useCancellationPolicy(
    listing?.cancellationPolicyId,
  );

  const onEditCart: typeof editCart = (cartData, route, overrideSearch) => {
    if (cartData.state === CartItemState.READY) {
      const data = getAnalyticsDataFromCartItem({
        cartData,
        listing,
        priceByTierName: stayPrices,
      });

      analyticsManager.sendEvent(
        route?.includes('checkout')
          ? ANALYTICS_EVENT.CLICK_PROCEED_TO_CHECKOUT_BUTTON
          : ANALYTICS_EVENT.CLICK_ADD_TO_CART_BUTTON,
        data,
      );
    }

    return editCart(cartData, route, overrideSearch);
  };

  const analyticsItems = useMemo(() => {
    const initialData = {
      ids: [] as string[],
      titles: [] as string[],
      categories: [] as string[],
      items: [] as AnalyticsProduct[],
    };

    return (
      listing?.addOns?.reduce((acc, addOn, index) => {
        acc.items.push({
          id: addOn?.listingAddOnId ?? '',
          index,
          title: addOn?.label ?? '',
          category: listing.category?.name,
          priceTiers: [
            {
              name: '',
              price: addOn?.price ?? 0,
            },
          ],
        });
        acc.ids.push(addOn?.addOnId ?? '');
        acc.titles.push(addOn?.label ?? '');

        acc.categories.push(listing.category?.name ?? '');
        return acc;
      }, initialData) ?? initialData
    );
  }, [listing]);

  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_ADDONS,
    payload: {
      [ANALYTICS_PROPERTY.AllExperiences]: analyticsItems.titles.join(';'),
      [ANALYTICS_PROPERTY.AllCategories]:
        analyticsItems.categories.join(';') || '',
      [ANALYTICS_PROPERTY.ExperienceCount]:
        analyticsItems.items?.length?.toString(),
      [ANALYTICS_PROPERTY.Products]: JSON.stringify(analyticsItems.items) ?? [],
      [ANALYTICS_PROPERTY.PageName]: `${translate('addOns')} | ${
        listing?.title
      } | ${brand.settings.name}`,
      [ANALYTICS_PROPERTY.PageType]: 'AddOns Page',
      [ANALYTICS_PROPERTY.ExperienceTitle]: listing?.title ?? '',
    },
    optionalPayload: {
      [ANALYTICS_PROPERTY.ListId]:
        cartItem?.groupId ?? cartItem?.experienceId ?? listing?.id,
      [ANALYTICS_PROPERTY.ListName]: 'AddOns',
    },
    isNotReady: !listing?.addOns?.length || !brand.settings || !listing?.title,
  });

  useEffect(() => {
    setAddOnsAmounts(
      (cartItem?.addOns || []).reduce((acc, addOn) => {
        acc[addOn.addOnId] = addOn.selectedNumber;
        return acc;
      }, {} as Record<string, number>),
    );
  }, [cartItem]);

  const availabilityByListingAddOnId = new Map(
    addOnAvailability?.items.map((item) => [item.listingAddOnId, item]),
  );
  const displayedAddOns = (listing?.addOns ?? []).flatMap((addOn) => {
    if (!addOn) return [];
    const availability = availabilityByListingAddOnId.get(addOn.listingAddOnId);

    return [
      {
        ...addOn,
        limit: availability?.availableQuantity ?? addOn.limit,
      } satisfies AddOnV3,
    ];
  });
  const unavailableReasons = new Map<string, ListingAddOnUnavailableReason>();
  if (cartItem) {
    displayedAddOns
      .filter(
        (addOn) =>
          !availabilityByListingAddOnId.has(addOn.listingAddOnId) &&
          !isAddOnBookable(addOn, cartItem),
      )
      .forEach(({ listingAddOnId }) => {
        unavailableReasons.set(
          listingAddOnId,
          ListingAddOnUnavailableReason.BOOKING_CUTOFF,
        );
      });
  }
  addOnAvailability?.items.forEach((item) => {
    if (!item.available) {
      unavailableReasons.set(
        item.listingAddOnId,
        item.unavailableReason ?? ListingAddOnUnavailableReason.SOLD_OUT,
      );
    }
  });
  const unavailableAddOnIds = new Set(unavailableReasons.keys());

  const cartItemDate = useMemo(() => {
    return moment(cartItem?.sessionDateTime.split('T')[0]).format(
      'ddd, MMMM Do',
    );
  }, [cartItem?.sessionDateTime]);

  if (isLoading || isLoadingAddOnAvailability || !listing) {
    return <AddOnsPageLoading />;
  }

  if (!cartItem) {
    return (
      <Wrapper>
        <LayoutWrapper>
          <NavigationHeader />
        </LayoutWrapper>
      </Wrapper>
    );
  }

  const selectedAddOns = displayedAddOns.flatMap((addOn) => {
    if (
      !unavailableAddOnIds.has(addOn.listingAddOnId) &&
      addOnsAmounts[addOn.listingAddOnId]
    ) {
      return {
        id: addOn?.listingAddOnId,
        addOnId: addOn?.addOnId,
        label: addOn?.label,
        selectedNumber: addOnsAmounts[addOn?.listingAddOnId ?? ''],
        price: addOn?.price,
        taxes: addOn?.taxes,
      };
    }
    return [];
  });

  const onAddToCart = () => {
    if (!cartItem) return;
    onEditCart(
      {
        ...cartItem,
        state: CartItemState.READY,
        addOns: selectedAddOns,
      },
      '/',
    );
  };

  const onCheckout = () => {
    if (!cartItem) return;

    const params = new URLSearchParams();
    Object.keys(searchParams).forEach((key) => {
      params.set(key, `${searchParams[key]}`);
    });

    onEditCart(
      {
        ...cartItem,
        state: CartItemState.READY,
        addOns: selectedAddOns,
      },
      '/checkout',
      params.toString(),
    );
  };

  const goToParticipantsPage = () => {
    if (!cartItem) return;
    const params = new URLSearchParams();
    params.set('skipParticipantsName', askParticipantsInfo ? 'false' : 'true');
    Object.keys(searchParams).forEach((key) => {
      params.set(key, `${searchParams[key]}`);
    });

    onEditCart(
      {
        ...cartItem,
        state: CartItemState.PENDING,
        addOns: selectedAddOns,
      },
      `/e/${cartItem.experienceId}/participants/${cartItem.cartItemId}`,
      params.toString(),
    );
  };

  return (
    <Wrapper>
      {listing?.title && (
        <PageTitle subTitle="addOns" translatedTitle={listing?.title} />
      )}
      <LayoutWrapper>
        <NavigationHeader />
        <PageWithSidebar>
          <MainContent>
            <Flex direction="column" alignItem="flex-start" gap={6}>
              <ResourceHeading2 fontSize="28px" className="add-ons-page-title">
                {listing?.title}
              </ResourceHeading2>
              <ResourceParagraph
                className="current-date"
                fontSize="16px"
                opacity="0.7"
                fontWeight="400"
                lineHeight="20px"
              >
                {cartItemDate}
              </ResourceParagraph>
            </Flex>

            <Sep />

            <AddOnsWrapper>
              <AddOnsList
                addOns={displayedAddOns}
                unavailableReasons={unavailableReasons}
                selectedPriceTiersCount={cartItem?.participants.length || 1}
                addOnsAmounts={addOnsAmounts}
                onAddOnsAmountsChange={setAddOnsAmounts}
                columns={isMobile || isTablet ? 1 : 2}
              />
            </AddOnsWrapper>

            <Sep />

            {hasCustomQuestions || askParticipantsInfo || hasResourceOptions ? (
              <PrimaryButton
                big
                className="continue-button"
                onClick={goToParticipantsPage}
              >
                {translate('continue')}
              </PrimaryButton>
            ) : (
              <ButtonsWrapper>
                {!hasRemoveAddToCart && (
                  <SecondaryButton
                    big
                    disabled={isLoadingResourceOptions}
                    onClick={onAddToCart}
                    className="add-to-cart-button"
                  >
                    {translate('addToCart')}
                  </SecondaryButton>
                )}

                <PrimaryButton
                  big
                  className="checkout-button"
                  disabled={isLoadingResourceOptions}
                  onClick={onCheckout}
                >
                  {translate('checkout')}
                </PrimaryButton>
              </ButtonsWrapper>
            )}
          </MainContent>

          {cartItem && listing && (
            <ListingCartItemSummary
              cartItem={cartItem}
              listing={listing}
              overriddenAddOns={selectedAddOns}
              cancellationPolicy={cancellationPolicy}
            />
          )}
        </PageWithSidebar>
      </LayoutWrapper>
    </Wrapper>
  );
};

export default SelectAddOnsPage;

const LayoutWrapper = styled.div`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;

  > div {
    flex-shrink: 0;
    flex-grow: 0;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  max-width: calc(100% - 350px - 72px);
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 36px;

  @media (max-width: 768px) {
    max-width: unset;
  }
`;

const AddOnsWrapper = styled.div`
  width: 100%;
  max-height: calc(100vh - 400px);
  min-height: 270px;
  overflow-y: auto;

  @media (max-width: 768px) {
    max-height: unset;
    height: fit-content;
    overflow-y: unset;
  }
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  & button {
    width: 50%;
  }
`;
